import {
  isBoldGlyph,
  randomGlyph,
  BOLD_POOL,
  MEDIUM_POOL,
  THIN_POOL,
} from "../art/pastiradit";
import {
  ALPHA_MIN,
  ALPHA_MAX,
  SHADOW_ALPHA,
  FONT_STACK,
  LIGHT_X,
  LIGHT_Y,
  LIGHT_Z,
  SHADOW_DIR_X,
  SHADOW_DIR_Y,
  ink,
} from "./theme";

export type Eye = {
  opacity: number;
  update: (
    dt: number,
    pointerX: number,
    pointerY: number,
    cx: number,
    cy: number,
  ) => void;
  draw: (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    scale: number,
  ) => void;
};

const SHADOW_STEP = 1.5;
const FLICKER_MIN = 0.04;
const FLICKER_MAX = 0.14;
const FONT_MIN = 3;

/** Tall rounded-rect; cells stay roughly square. */
const EYE_COLS = 28;
const EYE_ROWS = 56;
const CELL_COUNT = EYE_COLS * EYE_ROWS;
const EYE_COUNT = 2;
const EYE_ASPECT = 0.48;

const LERP = 0.1;
const MAX_LOOK_X = 0.2;
const MAX_LOOK_Y = 0.22;
const REST_LOOK_Y = -0.16;
const PUPIL_HALF_H = 0.16;
const PUPIL_HW = 0.15;
const PUPIL_HH = 0.18;
const PUPIL_RADIUS = 0.3;
const GAP_RATIO = 0.28;
const MID_RATIO = 0.7;
const MID_HALF = 0.94;
const TIP_MIN = MID_HALF * 0.78;
const LID_REST = 0.3;
const LID_CLOSED = -0.84;
const LID_DROOP = 0.14;

const BLINK_INTERVAL_MIN = 3.2;
const BLINK_INTERVAL_MAX = 5.5;
const BLINK_DUR = 0.28;
const OCEAN_POOL = ".:'`";

type Region = "sclera" | "iris" | "pupil" | "lid";

type FlickerCell = {
  current: string;
  ttl: number;
  region: Region;
  on: boolean;
  shade: number;
};

function nextTtl(rng = Math.random): number {
  return FLICKER_MIN + rng() * (FLICKER_MAX - FLICKER_MIN);
}

function hash01(col: number, row: number): number {
  const n = Math.sin(col * 12.9898 + row * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

function pickPupil(shade: number): string {
  return shade > 0.25 ? randomGlyph(BOLD_POOL) : randomGlyph(MEDIUM_POOL);
}

function pickIris(shade: number): string {
  if (shade > 0.55) return randomGlyph(BOLD_POOL);
  if (shade > 0.28) return randomGlyph(MEDIUM_POOL);
  return randomGlyph(THIN_POOL);
}

function pickSclera(shade: number): string {
  if (shade > 0.55 && Math.random() < 0.4) return ":";
  return randomGlyph(OCEAN_POOL);
}

function pickLid(shade: number): string {
  if (shade > 0.4) return randomGlyph(BOLD_POOL);
  return randomGlyph(MEDIUM_POOL);
}

function pickGlyph(region: Region, shade: number): string {
  if (region === "pupil") return pickPupil(shade);
  if (region === "iris") return pickIris(shade);
  if (region === "lid") return pickLid(shade);
  return pickSclera(shade);
}

function keepSclera(
  col: number,
  row: number,
  r2: number,
  shade: number,
): boolean {
  if (r2 > 0.76) return true;
  const floor = 0.46 + (1 - shade) * 0.22;
  return hash01(col, row) > floor;
}

/** Straight sides, blunt rounded caps. */
function capsuleHalfWidth(vyAbs: number): number {
  if (vyAbs <= MID_RATIO) return MID_HALF;
  const t = Math.max(
    0,
    Math.min(1, (vyAbs - MID_RATIO) / (1 - MID_RATIO)),
  );
  const round = Math.sqrt(Math.max(0, 1 - t * t));
  return TIP_MIN + (MID_HALF - TIP_MIN) * round;
}

function lidEdge(vx: number, lidY: number): number {
  const nx = Math.max(-1, Math.min(1, vx / MID_HALF));
  return lidY - LID_DROOP * (1 - nx * nx);
}

/** CSS border-radius 30%: rx = 30% width, ry = 30% height. */
function inRoundedRect(
  dx: number,
  dy: number,
  hw: number,
  hh: number,
): boolean {
  const rx = PUPIL_RADIUS * 2 * hw;
  const ry = PUPIL_RADIUS * 2 * hh;
  const ix = Math.max(0, hw - rx);
  const iy = Math.max(0, hh - ry);
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (ax > hw || ay > hh) return false;
  if (ax <= ix || ay <= iy) return true;
  const cx = (ax - ix) / rx;
  const cy = (ay - iy) / ry;
  return cx * cx + cy * cy <= 1;
}

type Sample = {
  region: Region;
  shade: number;
  r2: number;
};

function sampleCell(
  col: number,
  row: number,
  pupilX: number,
  pupilY: number,
  lidY: number,
): Sample | null {
  const vx = ((col + 0.5) / EYE_COLS) * 2 - 1;
  const vy = -(((row + 0.5) / EYE_ROWS) * 2 - 1);
  const yAbs = Math.abs(vy);
  if (yAbs >= 1) return null;
  const hw = capsuleHalfWidth(yAbs);
  if (Math.abs(vx) > hw) return null;

  let nx = hw > 1e-6 ? Math.max(-1, Math.min(1, vx / hw)) : 0;
  let ny = 0;
  let nz = Math.sqrt(Math.max(0, 1 - nx * nx));
  if (yAbs > MID_RATIO) {
    const t = (yAbs - MID_RATIO) / (1 - MID_RATIO);
    ny = Math.sign(vy) * t * 0.5;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
  }
  const shade = Math.max(0, nx * LIGHT_X + ny * LIGHT_Y + nz * LIGHT_Z);

  const radial = hw > 1e-6 ? Math.abs(vx) / hw : 1;
  const capT =
    yAbs > MID_RATIO ? (yAbs - MID_RATIO) / (1 - MID_RATIO) : 0;
  const r2 = Math.max(radial * radial, capT * capT);

  if (vy > lidEdge(vx, lidY)) {
    return { region: "lid", shade, r2 };
  }

  const dx = (vx - pupilX) * EYE_ASPECT;
  const dy = vy - pupilY;
  const pupilCol = Math.round(((pupilX + 1) / 2) * EYE_COLS - 0.5);
  let region: Region = "sclera";
  if (col === pupilCol && Math.abs(vy - pupilY) <= PUPIL_HALF_H) {
    region = "pupil";
  } else if (inRoundedRect(dx, dy, PUPIL_HW, PUPIL_HH)) {
    region = "iris";
  }

  return { region, shade, r2 };
}

function eyeCenters(cx: number, scale: number): readonly [number, number] {
  const width = scale * EYE_ASPECT;
  const gap = width * GAP_RATIO;
  const offset = (width + gap) / 2;
  return [cx - offset, cx + offset];
}

export function createEye(): Eye {
  let opacity = 1;
  let time = 0;
  let lookX = 0;
  let lookY = REST_LOOK_Y;
  let targetX = 0;
  let targetY = 0;
  let blinkT = -1;
  let nextBlink =
    BLINK_INTERVAL_MIN +
    Math.random() * (BLINK_INTERVAL_MAX - BLINK_INTERVAL_MIN);

  const cells: FlickerCell[] = Array.from(
    { length: EYE_COUNT * CELL_COUNT },
    () => ({
      current: ".",
      ttl: nextTtl(),
      region: "sclera",
      on: false,
      shade: 0,
    }),
  );

  const seedCell = (
    cell: FlickerCell,
    sample: Sample,
    col: number,
    row: number,
  ) => {
    if (
      sample.region === "sclera" &&
      !keepSclera(col, row, sample.r2, sample.shade)
    ) {
      cell.on = false;
      cell.current = "";
      cell.region = "sclera";
      cell.shade = sample.shade;
      return;
    }
    cell.on = true;
    cell.region = sample.region;
    cell.shade = sample.shade;
    cell.current = pickGlyph(sample.region, sample.shade);
    cell.ttl = nextTtl();
  };

  const lidYFromBlink = (): number => {
    if (blinkT < 0) return LID_REST;
    const pulse = Math.sin(blinkT * Math.PI);
    return LID_REST + (LID_CLOSED - LID_REST) * pulse;
  };

  const update: Eye["update"] = (dt, pointerX, pointerY, cx, cy) => {
    time += dt;

    const dx = pointerX - cx;
    const dy = pointerY - cy;
    const dist = Math.hypot(dx, dy) || 1;
    const reach = Math.min(1, dist / 280);
    targetX = (dx / dist) * reach * MAX_LOOK_X;
    targetY = REST_LOOK_Y - (dy / dist) * reach * MAX_LOOK_Y;

    const k = 1 - Math.pow(1 - LERP, dt * 60);
    lookX += (targetX - lookX) * k;
    lookY += (targetY - lookY) * k;

    if (blinkT >= 0) {
      blinkT += dt / BLINK_DUR;
      if (blinkT >= 1) {
        blinkT = -1;
        nextBlink =
          BLINK_INTERVAL_MIN +
          Math.random() * (BLINK_INTERVAL_MAX - BLINK_INTERVAL_MIN);
      }
    } else {
      nextBlink -= dt;
      if (nextBlink <= 0) blinkT = 0;
    }

    const lidY = lidYFromBlink();
    const maxLookY = lidEdge(0, lidY) - PUPIL_HH - 0.04;
    lookX = Math.max(-MAX_LOOK_X, Math.min(MAX_LOOK_X, lookX));
    lookY = Math.max(-0.72, Math.min(maxLookY, lookY));
    const pupilX = lookX;
    const pupilY = lookY;

    for (let eye = 0; eye < EYE_COUNT; eye++) {
      const base = eye * CELL_COUNT;
      for (let row = 0; row < EYE_ROWS; row++) {
        for (let col = 0; col < EYE_COLS; col++) {
          const cell = cells[base + row * EYE_COLS + col]!;
          const sample = sampleCell(col, row, pupilX, pupilY, lidY);
          if (!sample) {
            cell.on = false;
            cell.current = "";
            continue;
          }

          cell.shade = sample.shade;
          const visible =
            sample.region !== "sclera" ||
            keepSclera(col, row, sample.r2, sample.shade);
          if (!visible) {
            cell.on = false;
            cell.current = "";
            cell.region = "sclera";
            continue;
          }

          if (!cell.on || cell.region !== sample.region) {
            seedCell(cell, sample, col, row);
            continue;
          }

          cell.ttl -= dt;
          if (cell.ttl <= 0) {
            cell.current = pickGlyph(sample.region, sample.shade);
            cell.ttl = nextTtl();
          }
        }
      }
    }
  };

  const draw: Eye["draw"] = (ctx, cx, cy, scale) => {
    if (opacity <= 0.001) return;

    const height = scale;
    const width = scale * EYE_ASPECT;
    const cellW = width / EYE_COLS;
    const cellH = height / EYE_ROWS;
    const fontSize = Math.max(FONT_MIN, Math.min(cellW, cellH) * 0.92);
    const shadowDx = SHADOW_DIR_X * SHADOW_STEP;
    const shadowDy = SHADOW_DIR_Y * SHADOW_STEP;
    const [leftCx, rightCx] = eyeCenters(cx, scale);
    const origins = [leftCx, rightCx];

    const breath = 0.5 + 0.5 * Math.sin(time * 0.55);
    const mainA = (ALPHA_MIN + (ALPHA_MAX - ALPHA_MIN) * breath) * opacity;
    const mediumFont = `500 ${fontSize}px ${FONT_STACK}`;
    const boldFont = `700 ${fontSize}px ${FONT_STACK}`;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (let eye = 0; eye < EYE_COUNT; eye++) {
      const base = eye * CELL_COUNT;
      const originX = origins[eye]! - width / 2;
      const originY = cy - height / 2;

      ctx.font = mediumFont;
      ctx.fillStyle = ink(SHADOW_ALPHA * opacity);
      for (let row = 0; row < EYE_ROWS; row++) {
        for (let col = 0; col < EYE_COLS; col++) {
          const cell = cells[base + row * EYE_COLS + col]!;
          if (!cell.on || !cell.current) continue;
          const x = originX + (col + 0.5) * cellW + shadowDx;
          const y = originY + (row + 0.5) * cellH + shadowDy;
          ctx.fillText((row + col) % 2 === 0 ? "." : ":", x, y);
        }
      }

      for (let row = 0; row < EYE_ROWS; row++) {
        for (let col = 0; col < EYE_COLS; col++) {
          const cell = cells[base + row * EYE_COLS + col]!;
          if (!cell.on || !cell.current) continue;
          const a = mainA * (0.52 + 0.48 * cell.shade);
          const x = originX + (col + 0.5) * cellW;
          const y = originY + (row + 0.5) * cellH;
          ctx.font = isBoldGlyph(cell.current) ? boldFont : mediumFont;
          ctx.fillStyle = ink(a);
          ctx.fillText(cell.current, x, y);
        }
      }
    }

    ctx.restore();
  };

  return {
    get opacity() {
      return opacity;
    },
    set opacity(v: number) {
      opacity = Math.max(0, Math.min(1, v));
    },
    update,
    draw,
  };
}

export function eyeScale(viewportW: number, viewportH: number): number {
  return Math.min(viewportW, viewportH) * 0.58;
}
